import { BsChevronDown } from "react-icons/bs";
import { BsChevronUp } from "react-icons/bs";
import { Box, Tooltip, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { AiSearchDocumentInfo } from "../../../api/types/chatTypes";
import CitationItem from "./CitationItem";
import SourceLabel from "./SourceLabel";
import { useTranslation } from "react-i18next";
import IconWrapper from "../../Common/IconWrapper";

type CitationListProps = {
    usedSources: AiSearchDocumentInfo[] | undefined;
    unusedSources: AiSearchDocumentInfo[] | undefined;
    onCitationClicked: (source: AiSearchDocumentInfo) => void;
};

const ExpandMoreIcon = IconWrapper(BsChevronDown);
const ExpandLessIcon = IconWrapper(BsChevronUp);

/**
 * CitationList Component
 *
 * Displays a list of citations and unused sources, allowing users to click on a source to view it.
 * If there are additional unused sources, a "További találatok" (More results) button is displayed
 * to reveal them.
 *
 *
 * @component
 * @param {CitationListProps} props - The props for the component.
 * @returns {JSX.Element} A component that renders a list of citations.
 *  * @example
 * ```tsx
 * <CitationList
 *   citations={["/example_1-2.pdf", "/example_3-4.pdf"]}
 *   unusedSources={["/example_5-6.pdf", "/example_7-8.pdf"]}
 *   onCitationClicked={(filePath) => console.log(filePath)}
 * />
 * ```
 */
function CitationList({ usedSources, unusedSources, onCitationClicked }: CitationListProps) {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "chat.citationList" });
    const isDark = theme.palette.mode === "dark";

    const [hoveredCitationDisplayName, setHoveredCitationDisplayName] = useState<string | null>(null);
    const [expanded, setExpanded] = useState(false);

    const handleToggle = () => setExpanded(!expanded);

    // Apply theme-based base and hover styles to all citation elements and update on theme change
    useEffect(() => {
        if (!usedSources) return;

        const attachedListeners: { el: HTMLElement; enter: () => void; leave: () => void }[] = [];

        usedSources.forEach(citation => {
            const elements = document.querySelectorAll<HTMLElement>(`[data-tag="${citation.citation_display_name}"]`);

            elements.forEach(element => {
                // Apply initial styling
                element.style.color = theme.palette.text.primary;
                element.style.backgroundColor = theme.palette.citationBackground;

                // Define handlers
                const handleMouseEnter = () => {
                    element.style.color = theme.palette.text.primary;
                    element.style.backgroundColor = theme.palette.citationBackgroundHover;
                };

                const handleMouseLeave = () => {
                    element.style.color = theme.palette.text.primary;
                    element.style.backgroundColor = theme.palette.citationBackground;
                };

                // Attach events
                element.addEventListener("mouseenter", handleMouseEnter);
                element.addEventListener("mouseleave", handleMouseLeave);

                // Save for cleanup
                attachedListeners.push({ el: element, enter: handleMouseEnter, leave: handleMouseLeave });
            });
        });

        // Cleanup
        return () => {
            attachedListeners.forEach(({ el, enter, leave }) => {
                el.removeEventListener("mouseenter", enter);
                el.removeEventListener("mouseleave", leave);
            });
        };
    }, [usedSources, isDark]);

    // Highlight the currently hovered citation by ID and revert on unhover
    useEffect(() => {
        if (!hoveredCitationDisplayName) return;

        const elements = document.querySelectorAll<HTMLElement>(`[data-tag="${hoveredCitationDisplayName}"]`);

        elements.forEach(element => {
            if (element) element.style.backgroundColor = theme.palette.citationBackgroundHover;
        });

        return () => {
            elements.forEach(element => {
                element.style.backgroundColor = theme.palette.citationBackground;
            });
        };
    }, [hoveredCitationDisplayName]);

    const mergedCitationListSet = Array.from(new Set([...(usedSources ?? []), ...(unusedSources ?? [])]));

    return (
        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", flexWrap: "wrap" }}>
            <Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        fontWeight: 500,
                        fontSize: "10px",
                        marginRight: "0px",
                        paddingRight: "0px"
                    }}
                    onClick={handleToggle}
                >
                    <SourceLabel text={t("sources")} sx={{ marginRight: "8px", marginTop: "0px", fontSize: "14px", color: theme.palette.citationsLabel }} />
                    {expanded ? (
                        <Tooltip title={t("hideSources")} placement="top">
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                                <ExpandLessIcon style={{ fontSize: "18px", color: theme.palette.citationsLabel }} />
                            </span>
                        </Tooltip>
                    ) : (
                        <Tooltip title={t("showSources")} placement="top">
                            <span style={{ display: "inline-flex", alignItems: "center" }}>
                                <ExpandMoreIcon style={{ fontSize: "18px", color: theme.palette.citationsLabel }} />
                            </span>
                        </Tooltip>
                    )}
                </Box>

                {/* Sources shown in a row when expanded */}
                {expanded && (
                    <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                        {mergedCitationListSet.map((path, i) => {
                            return <CitationItem key={i} source={path} onClick={onCitationClicked} setHoveredPath={setHoveredCitationDisplayName} />;
                        })}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

export default CitationList;
